import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaCheck, FaTruck, FaWalking } from "react-icons/fa";
import {
  Box,
  Button,
  Flex,
  Text,
  Icon,
  VStack,
  Collapse,
  useToast,
} from "@chakra-ui/react";
import { DeliveryModes, OrderStatus } from "../views/Orders/enums";

interface DeliveryModeSelectorProps {
  selectedMode: string;
  onChange: (mode: string) => void;
  onUpdate?: (mode: string) => Promise<void>;
  isCustomerOrderFreezed?: boolean;
  orderStatus?: string;
}

const deliveryOptions = [
  {
    id: "door",
    name: "Door Delivery",
    icon: FaTruck,
    description: "Delivery right to your doorstep",
    value: DeliveryModes.DOOR_DELIVERY,
  },
  {
    id: "pickup",
    name: "Self Pickup",
    icon: FaWalking,
    description: "Collect from nearest pickup point",
    value: DeliveryModes.SELF_PICKUP,
  },
];

/**
 * Formats delivery mode enum value to display label.
 * e.g., "SELF_PICKUP" -> "Self Pickup"
 */
export const formatDeliveryModeLabel = (deliveryMode: string): string => {
  return deliveryMode
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const DeliveryModeSelector: React.FC<DeliveryModeSelectorProps> = ({
  selectedMode,
  onChange,
  onUpdate,
  isCustomerOrderFreezed = false,
  orderStatus,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isDisabled =
    isCustomerOrderFreezed || orderStatus === OrderStatus.DELIVERED;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const selectedOption = deliveryOptions.find(
    (opt) => opt.value === selectedMode
  );

  const blue700 = "rgb(59, 132, 246)";
  const blue500 = "rgb(0, 194, 228)";
  const bgGradient = `linear(to-r, ${blue700} 0%, ${blue500} 51%, ${blue700} 100%)`;

  const handleDeliveryModeChange = async (
    option: (typeof deliveryOptions)[0]
  ) => {
    if (isCustomerOrderFreezed) {
      toast({
        title: "Cannot update delivery mode",
        description: "This order is frozen and cannot be modified",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (selectedMode === option.value) {
      setIsOpen(false);
      return;
    }

    try {
      setIsUpdating(true);

      if (onUpdate) {
        await onUpdate(option.value);
      }

      onChange(option.value);
      toast({
        title: "Delivery mode updated",
        description: `Successfully changed to ${option.name}`,
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error updating delivery mode",
        description: "Failed to update delivery mode. Please try again.",
        status: "error",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
      setIsOpen(false);
    }
  };

  return (
    <Box
      position="relative"
      width="auto"
      minWidth="fit-content"
      ref={dropdownRef}
    >
      {/* Toggle Button */}
      <Button
        onClick={toggleDropdown}
        aria-label={`Current delivery mode: ${
          selectedOption?.name || "None selected"
        }`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        backgroundSize="200%"
        transition="background-position 0.3s ease"
        backgroundPosition="left center"
        color={"white"}
        bgGradient={bgGradient}
        _hover={{
          backgroundPosition: "right center",
        }}
        py={1}
        height={"100%"}
        px={1}
        borderRadius="md"
        boxShadow="sm"
        _active={{ transform: "translateY(-1px)" }}
        fontSize="xs"
        fontWeight="semibold"
        whiteSpace="nowrap"
        isDisabled={isDisabled}
        rightIcon={
          <Icon
            as={FaChevronDown}
            transform={isOpen ? "rotate(180deg)" : "none"}
            transition="transform 0.2s"
            w={3}
            h={3}
          />
        }
      >
        <Flex align="center" gap={2}>
          <Flex
            w={5}
            h={4}
            bg="white"
            rounded="md"
            align="center"
            justify="center"
          >
            {selectedOption && (
              <Icon
                as={selectedOption.icon}
                color={"rgb(59, 132, 246)"}
                w={"70%"}
                h={"70%"}
              />
            )}
          </Flex>
          <Text fontWeight="bold" fontSize="x-small" textTransform="uppercase">
            {selectedOption?.name || "-"}
          </Text>
        </Flex>
      </Button>

      {/* Dropdown */}
      <Collapse in={isOpen} unmountOnExit>
        <Box
          position="absolute"
          right={0}
          mt={3}
          w={72}
          bg="white"
          rounded="xl"
          shadow="xl"
          border="1px solid"
          borderColor="gray.100"
          zIndex={10}
          overflow="hidden"
        >
          <Box
            px={4}
            py={3}
            bgGradient="linear(to-r, blue.50, blue.100)"
            borderBottom="1px solid"
            borderColor="gray.100"
          >
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Select Delivery Mode
            </Text>
          </Box>

          <VStack spacing={0} align="stretch">
            {deliveryOptions.map((option) => {
              const isSelected = selectedMode === option.value;
              return (
                <Flex
                  key={option.id}
                  px={4}
                  py={3.5}
                  align="center"
                  cursor="pointer"
                  bg={isSelected ? "blue.50" : "transparent"}
                  borderLeft={isSelected ? "4px solid" : "none"}
                  borderColor={isSelected ? blue700 : "transparent"}
                  _hover={isSelected ? { bg: "blue.50" } : { bg: "gray.50" }}
                  position="relative"
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleDeliveryModeChange(option);
                    }
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeliveryModeChange(option);
                  }}
                >
                  <Flex
                    w={10}
                    h={10}
                    rounded="full"
                    align="center"
                    justify="center"
                    bg={isSelected ? "blue.100" : "gray.100"}
                  >
                    <Icon
                      as={option.icon}
                      color={isSelected ? blue700 : "gray.500"}
                      w={5}
                      h={5}
                    />
                  </Flex>

                  <Box ml={4} flex="1">
                    <Text
                      fontWeight={isSelected ? "bold" : "normal"}
                      color={isSelected ? blue700 : "gray.700"}
                      textTransform="uppercase"
                    >
                      {option.name}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={0.5}>
                      {option.description}
                    </Text>
                  </Box>

                  {isSelected && (
                    <Flex
                      w={6}
                      h={6}
                      rounded="full"
                      bg={blue700}
                      align="center"
                      justify="center"
                      ml={2}
                    >
                      <Icon as={FaCheck} color="white" w={3} h={3} />
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </VStack>
        </Box>
      </Collapse>
    </Box>
  );
};
